import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import NoClientState from './NoClientState';
import './ai-vis.css';

const PAGE_SIZES = [20, 40, 50, 100];

const SERVICE_LABELS = {
  chatgpt:             'ChatGPT',
  perplexity:          'Perplexity',
  google_ai_overviews: 'Google AI Overviews',
};

/**
 * Format Cloro model slugs into readable labels.
 *   gpt-5-5      → GPT 5.5
 *   gpt-5-3-mini → GPT 5.3 mini
 */
function formatModelName(raw) {
  if (!raw) return '';
  const lower = raw.toLowerCase().trim();
  if (lower.startsWith('gpt-')) {
    const parts = lower.slice(4).split('-');
    const numericParts = [];
    const suffixParts  = [];
    let inSuffix = false;
    for (const p of parts) {
      if (!inSuffix && /^\d+$/.test(p)) {
        numericParts.push(p);
      } else {
        inSuffix = true;
        suffixParts.push(p);
      }
    }
    const version = numericParts.join('.');
    const suffix  = suffixParts.join(' ');
    const label   = [version, suffix].filter(Boolean).join(' ');
    return `GPT ${label}`.trim();
  }
  return raw;
}

function serviceIconHref(key) {
  if (key === 'chatgpt')             return '/chatgpt-icon.png';
  if (key === 'perplexity')          return '/perplexity-icon.png';
  if (key === 'google_ai_overviews') return '/google-icon.png';
  return null;
}

function formatRelativeDate(iso) {
  if (!iso) return '—';
  try {
    const d   = new Date(iso);
    const diff = Date.now() - d.getTime();
    const sec  = Math.floor(diff / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min. ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr. ago`;
    const days = Math.floor(hr / 24);
    if (days < 14) return `${days} day${days === 1 ? '' : 's'} ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

async function exportResponsesCsv(campaignId, clientName) {
  try {
    const { data } = await api.get(
      `/aivis/campaigns/${campaignId}/responses-export/`,
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(data);
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const safeName = (clientName || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}_Responses_${dd}${mm}${yyyy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    // silently fail — user can retry
  }
}

/* ── Fan-out queries mini modal ──────────────────────────────────────────── */
function FanOutQueriesModal({ queries, promptText, modelName, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="aivis-chat-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="aivis-foq-mini-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aivis-foq-mini-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="aivis-foq-mini-modal-head">
          <div>
            <div className="aivis-foq-mini-modal-title-row">
              <h3 id="aivis-foq-mini-title" className="aivis-foq-mini-modal-title">
                Fan-Out Queries
              </h3>
              {modelName ? (
                <span className="aivis-resp-model-badge" title={modelName}>
                  {formatModelName(modelName)}
                </span>
              ) : null}
            </div>
            {promptText ? (
              <p className="aivis-foq-mini-modal-sub" title={promptText}>
                {promptText.length > 100 ? `${promptText.slice(0, 100)}…` : promptText}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="aivis-chat-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <ul className="aivis-foq-mini-modal-list">
          {queries.map((q, i) => (
            <li key={i} className="aivis-foq-mini-modal-item">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden className="aivis-foq-mini-bullet">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {q}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ── Response execution modal ─────────────────────────────────────────────── */
function ResponseExecutionModal({ campaignId, executionId, promptText, onClose }) {
  const [detail,    setDetail]    = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!campaignId || !executionId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadError('');
      try {
        const { data } = await api.get(`/aivis/campaigns/${campaignId}/executions/${executionId}/`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setLoadError(e.response?.data?.detail || 'Could not load this response.');
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, executionId]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!executionId) return null;

  const svc      = detail?.service;
  const svcLabel = SERVICE_LABELS[svc] || svc || '';

  return (
    <div className="aivis-chat-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="aivis-chat-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aivis-resp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="aivis-chat-modal-head">
          <div className="aivis-chat-modal-head-left">
            {serviceIconHref(svc) ? (
              <img src={serviceIconHref(svc)} alt="" width={28} height={28} className="aivis-chat-modal-svc-icon" />
            ) : null}
            <span id="aivis-resp-modal-title" className="aivis-chat-modal-title">
              {loadError ? 'Response' : svcLabel}
            </span>
          </div>
          <div className="aivis-chat-modal-head-actions">
            {promptText ? (
              <span className="aivis-chat-modal-prompt-ref" title={promptText}>
                View prompt: {promptText.length > 80 ? `${promptText.slice(0, 80)}…` : promptText}
              </span>
            ) : null}
            <button type="button" className="aivis-chat-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>
        <div className="aivis-chat-modal-panes">
          <div className="aivis-chat-modal-main">
            <div className="aivis-chat-modal-user-bubble-row">
              <div className="aivis-chat-modal-user-bubble">{promptText || detail?.prompt_text || '—'}</div>
            </div>
            {loadError ? (
              <p className="aivis-chat-modal-error">{loadError}</p>
            ) : !detail ? (
              <p className="aivis-page-sub">Loading…</p>
            ) : detail.response_text ? (
              <div className="aivis-chat-modal-response aivis-markdown-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.response_text}</ReactMarkdown>
              </div>
            ) : (
              <div className="aivis-chat-modal-response">—</div>
            )}
          </div>
          <aside className="aivis-chat-modal-sources" aria-label="Sources">
            <h3 className="aivis-chat-modal-sources-title">Sources</h3>
            {!detail?.execution_sources?.length ? (
              <p className="aivis-prompts-muted">No sources for this response.</p>
            ) : (
              <ul className="aivis-chat-modal-source-list">
                {detail.execution_sources.map((s) => (
                  <li key={s.id} className="aivis-chat-modal-source-item">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aivis-chat-modal-source-link"
                    >
                      {s.page_title || s.url}
                    </a>
                    <span className="aivis-chat-modal-source-url">{s.url}</span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ── Sort icon ───────────────────────────────────────────────────────────── */
function SortIcon({ active, dir }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
      className={`aivis-comp-sort-icon${active ? ' aivis-comp-sort-icon--active' : ''}`}
    >
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity={active && dir === 'asc' ? 1 : 0.35} />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity={active && dir === 'desc' ? 1 : 0.35} />
    </svg>
  );
}

function SortHeader({ label, sortKey, currentSort, currentDir, onSort, centered }) {
  const active = currentSort === sortKey;
  return (
    <th
      className={`aivis-comp-th-sortable${centered ? ' aivis-comp-th-center aivis-foq-th-center' : ''}`}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span className="aivis-comp-th-inner">
        {label}
        <SortIcon active={active} dir={currentDir} />
      </span>
    </th>
  );
}

/* ── Skeleton rows ───────────────────────────────────────────────────────── */
function SkeletonRows({ count = 8 }) {
  return Array.from({ length: count }).map((_, i) => (
    <tr key={i} className="aivis-foq-skeleton-row">
      <td><div className="aivis-skeleton" style={{ width: '70%' }} /></td>
      <td><div className="aivis-skeleton" style={{ width: '40px' }} /></td>
      <td><div className="aivis-skeleton" style={{ width: '60%' }} /></td>
      <td><div className="aivis-skeleton" style={{ width: '80px' }} /></td>
      <td><div className="aivis-skeleton" style={{ width: '40px' }} /></td>
      <td><div className="aivis-skeleton" style={{ width: '70px' }} /></td>
    </tr>
  ));
}

/* ── Pagination bar ──────────────────────────────────────────────────────── */
function PaginationBar({ page, totalPages, pageSize, total, onPage, onPageSize }) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);
  return (
    <div className="aivis-foq-pagination">
      <span className="aivis-foq-pagination-info">
        {total === 0 ? 'No results' : `Showing ${start}–${end} of ${total} responses`}
      </span>
      <div className="aivis-foq-pagination-controls">
        <label className="aivis-foq-pagesize-label">
          Rows per page
          <select
            className="aivis-foq-pagesize"
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <span className="aivis-foq-page-indicator">
          Page {page} of {Math.max(totalPages, 1)}
        </span>
        <button
          type="button"
          className="aivis-foq-page-btn"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          className="aivis-foq-page-btn"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function ResponsesPage() {
  const { selectedCampaignId, campaigns } = useOutletContext();

  const campaign      = campaigns?.find((c) => String(c.id) === String(selectedCampaignId));
  const clientName    = campaign?.client_name || 'Client';
  const campaignReady = Boolean(selectedCampaignId);

  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search,   setSearch]   = useState('');
  const [service,  setService]  = useState('');
  const [sort,     setSort]     = useState('created_at');
  const [sortDir,  setSortDir]  = useState('desc');

  const [modalExId,     setModalExId]     = useState(null);
  const [modalPromptTx, setModalPromptTx] = useState('');
  const [fanoutModal,   setFanoutModal]   = useState(null); // { queries: [], promptText: '' }

  const fetchData = useCallback(async () => {
    if (!selectedCampaignId) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page:      page,
        page_size: pageSize,
      });
      if (service) params.set('service', service);
      const { data } = await api.get(
        `/aivis/campaigns/${selectedCampaignId}/responses/?${params.toString()}`
      );
      setRows(data.results  || []);
      setTotal(data.count   || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load responses.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCampaignId, page, pageSize, service]);

  // Refetch when campaign / page / page size / service filter change.
  useEffect(() => {
    setPage(1);
  }, [selectedCampaignId, pageSize, service]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Client-side search + sort on current page rows.
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let data = q
      ? rows.filter(
          (r) =>
            (r.response_preview || '').toLowerCase().includes(q) ||
            (r.prompt_text      || '').toLowerCase().includes(q) ||
            (r.model_name       || '').toLowerCase().includes(q)
        )
      : rows;

    const dir = sortDir === 'asc' ? 1 : -1;
    data = [...data].sort((a, b) => {
      if (sort === 'client_mentioned') {
        return dir * ((a.client_mentioned ? 1 : 0) - (b.client_mentioned ? 1 : 0));
      }
      if (sort === 'fan_out_count') {
        return dir * ((a.fan_out_count || 0) - (b.fan_out_count || 0));
      }
      if (sort === 'created_at') {
        return dir * (new Date(a.created_at) - new Date(b.created_at));
      }
      return 0;
    });
    return data;
  }, [rows, search, sort, sortDir]);

  function handleSort(key) {
    if (sort === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setSortDir('desc');
    }
  }

  function openModal(row) {
    setModalExId(row.id);
    setModalPromptTx(row.prompt_text || '');
  }

  const closeModal = useCallback(() => {
    setModalExId(null);
    setModalPromptTx('');
  }, []);

  if (!campaignReady) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-foq-page">
      {/* Header */}
      <div className="aivis-foq-header">
        <div>
          <h1 className="aivis-page-title">Responses</h1>
          <p className="aivis-page-sub">
            All completed AI responses for <strong>{clientName}</strong>.
            Click any row to view the full response and sources.
          </p>
        </div>
      </div>

      {/* Toolbar: search + service filter + export */}
      <div className="aivis-foq-toolbar">
        <div className="aivis-foq-search-wrap">
          <svg className="aivis-foq-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className="aivis-foq-search"
            placeholder="Search response, prompt, or model…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search responses"
          />
          {search && (
            <button
              type="button"
              className="aivis-foq-search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <select
          className="aivis-resp-service-filter"
          value={service}
          onChange={(e) => setService(e.target.value)}
          aria-label="Filter by service"
        >
          <option value="">All services</option>
          <option value="chatgpt">ChatGPT</option>
          <option value="google_ai_overviews">AI Overviews</option>
        </select>
        {total > 0 && (
          <button
            type="button"
            className="aivis-csv-export-btn"
            onClick={() => exportResponsesCsv(selectedCampaignId, clientName)}
            title="Export all responses as CSV"
            style={{ marginLeft: 'auto' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        )}
      </div>

      {/* Error */}
      {error ? (
        <div className="aivis-foq-error">{error}</div>
      ) : null}

      {/* Table */}
      <div className="aivis-foq-table-wrap">
        <table className="aivis-foq-table aivis-resp-table">
          <thead>
            <tr>
              <th className="aivis-comp-th-sortable">Response</th>
              <SortHeader label="Client Mentioned" sortKey="client_mentioned" currentSort={sort} currentDir={sortDir} onSort={handleSort} centered />
              <th>Prompt</th>
              <th>Model</th>
              <SortHeader label="Created" sortKey="created_at" currentSort={sort} currentDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows count={pageSize > 20 ? 20 : pageSize} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="aivis-foq-empty">
                  {search ? 'No responses match your search.' : 'No responses recorded yet.'}
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr
                  key={row.id}
                  className="aivis-recent-chats-tr"
                  role="button"
                  tabIndex={0}
                  onClick={() => openModal(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(row); }
                  }}
                >
                  {/* Response preview */}
                  <td className="aivis-resp-cell-response">
                    <div className="aivis-recent-chats-chat-inner">
                      <div className="aivis-recent-chats-service-wrap">
                        {serviceIconHref(row.service) ? (
                          <img
                            src={serviceIconHref(row.service)}
                            alt=""
                            width={22}
                            height={22}
                            className="aivis-recent-chats-service"
                          />
                        ) : null}
                      </div>
                      <span
                        className="aivis-recent-chats-preview"
                        title={row.response_preview || undefined}
                      >
                        {row.response_preview || '—'}
                      </span>
                    </div>
                  </td>

                  {/* Client Mentioned */}
                  <td className="aivis-recent-chats-mentioned">
                    {row.client_mentioned === true ? (
                      <span className="aivis-mentioned aivis-mentioned--yes">Yes</span>
                    ) : row.client_mentioned === false ? (
                      <span className="aivis-mentioned aivis-mentioned--no">No</span>
                    ) : (
                      <span className="aivis-prompts-muted">—</span>
                    )}
                  </td>

                  {/* Prompt */}
                  <td className="aivis-resp-cell-prompt">
                    {row.prompt_id ? (
                      <Link
                        to={`/ai-vis/prompts/${row.prompt_id}`}
                        className="aivis-foq-prompt-link"
                        onClick={(e) => e.stopPropagation()}
                        title={row.prompt_text || undefined}
                      >
                        {(row.prompt_text || '').length > 80
                          ? `${row.prompt_text.slice(0, 80)}…`
                          : row.prompt_text || '—'}
                      </Link>
                    ) : (
                      <span className="aivis-prompts-muted">—</span>
                    )}
                  </td>

                  {/* Model — GPT version for ChatGPT, service name for others */}
                  <td className="aivis-resp-cell-model">
                    {row.service === 'chatgpt' ? (
                      row.model_name ? (
                        <span className="aivis-resp-model-badge" title={row.model_name}>
                          {formatModelName(row.model_name)}
                        </span>
                      ) : (
                        <span className="aivis-prompts-muted">ChatGPT</span>
                      )
                    ) : (
                      <span className="aivis-prompts-muted">
                        {SERVICE_LABELS[row.service] || row.service || '—'}
                      </span>
                    )}
                  </td>

                  {/* Created */}
                  <td className="aivis-recent-chats-time">
                    {formatRelativeDate(row.created_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && total > 0 ? (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      ) : null}

      {/* Response detail modal */}
      {modalExId ? (
        <ResponseExecutionModal
          campaignId={selectedCampaignId}
          executionId={modalExId}
          promptText={modalPromptTx}
          onClose={closeModal}
        />
      ) : null}

      {/* Fan-out queries mini modal */}
      {fanoutModal ? (
        <FanOutQueriesModal
          queries={fanoutModal.queries}
          promptText={fanoutModal.promptText}
          modelName={fanoutModal.modelName}
          onClose={() => setFanoutModal(null)}
        />
      ) : null}
    </div>
  );
}
